// @file Locale-specific font URL overrides for tldraw whiteboard
// Source: https://airtable.com/appbKwco78ux3gFVG/tbldD6zxnMUSr3QkF/viwBqv0N39zyDLdME
//
// This module provides locale-aware font URLs for the tldraw editor.
// When a locale has font overrides, these URLs replace the default fonts
// in the ASSET_URLS.fonts configuration.
//
// To add a new locale:
//   1. Define font URLs for the locale (all 4 styles: draw, sans, mono, serif)
//   2. Add the locale to the switch statement in getLocaleFontUrls()

import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'

// Type for tldraw font asset URLs
type TldrawFontUrls = {
  tldraw_draw: string
  tldraw_draw_italic: string
  tldraw_draw_bold: string
  tldraw_draw_bold_italic: string
  tldraw_sans: string
  tldraw_sans_italic: string
  tldraw_sans_bold: string
  tldraw_sans_italic_bold: string
  tldraw_mono: string
  tldraw_mono_italic: string
  tldraw_mono_bold: string
  tldraw_mono_italic_bold: string
  tldraw_serif: string
  tldraw_serif_italic: string
  tldraw_serif_bold: string
  tldraw_serif_italic_bold: string
}

// ---------------------------------------------------------------------------
// Shared font URLs (used by multiple locales)
// ---------------------------------------------------------------------------

const CASCADIA_MONO = {
  variable: 'https://padlet.net/fonts/cascadia-mono/CascadiaMono-VF.ttf',
  variableItalic: 'https://padlet.net/fonts/cascadia-mono/CascadiaMono-Italic-VF.ttf',
} as const

const IBM_PLEX_MONO = {
  regular: 'https://padlet.net/fonts/ibm-plex-mono/IBMPlexMono-Regular.ttf',
  italic: 'https://padlet.net/fonts/ibm-plex-mono/IBMPlexMono-Italic.ttf',
  bold: 'https://padlet.net/fonts/ibm-plex-mono/IBMPlexMono-Bold.ttf',
  boldItalic: 'https://padlet.net/fonts/ibm-plex-mono/IBMPlexMono-BoldItalic.ttf',
} as const

const NOTO_SERIF = {
  regular: 'https://padlet.net/fonts/noto-serif/NotoSerif-VF.ttf',
  italic: 'https://padlet.net/fonts/noto-serif/NotoSerif-Italic-VF.ttf',
  bold: 'https://padlet.net/fonts/noto-serif/NotoSerif-VF.ttf',
  boldItalic: 'https://padlet.net/fonts/noto-serif/NotoSerif-Italic-VF.ttf',
} as const

const PLAYPEN_SANS_ARABIC = {
  variable: 'https://padlet.net/fonts/playpen-sans-arabic/PlaypenSansArabic-VF.ttf',
} as const

const NOTO_SANS_ARABIC = {
  variable: 'https://padlet.net/fonts/noto-sans/NotoSansArabic-VF.ttf',
} as const

const PLAYPEN_SANS = {
  variable: 'https://padlet.net/fonts/playpen-sans/PlaypenSans-VariableFont_wght.ttf',
} as const

// ---------------------------------------------------------------------------
// zh-CN (Simplified Chinese) fonts
// ---------------------------------------------------------------------------
// Playful: Alimama FangYuanTi (variable font, self-hosted)
// Modern: Noto Sans SC (variable font, self-hosted)
// Technical: Source Han Serif SC (variable font, self-hosted)
// Classic: Noto Serif SC (variable font, self-hosted)

const ZH_CN_FONTS: TldrawFontUrls = {
  // Playful - Alimama FangYuanTi (variable font covers all weights)
  tldraw_draw: 'https://padlet.net/fonts/alimama/AlimamaFangYuanTiVF-Thin.woff2',
  tldraw_draw_italic: 'https://padlet.net/fonts/alimama/AlimamaFangYuanTiVF-Thin.woff2',
  tldraw_draw_bold: 'https://padlet.net/fonts/alimama/AlimamaFangYuanTiVF-Thin.woff2',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/alimama/AlimamaFangYuanTiVF-Thin.woff2',

  // Modern - Noto Sans SC (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans-sc/NotoSansSC-VF.woff2',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans-sc/NotoSansSC-VF.woff2',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans-sc/NotoSansSC-VF.woff2',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans-sc/NotoSansSC-VF.woff2',

  // Technical - Source Han Serif SC (variable font covers all weights)
  tldraw_mono: 'https://padlet.net/fonts/source-han-serif-sc/SourceHanSerifSC-Regular.otf',
  tldraw_mono_italic: 'https://padlet.net/fonts/source-han-serif-sc/SourceHanSerifSC-Regular.otf',
  tldraw_mono_bold: 'https://padlet.net/fonts/source-han-serif-sc/SourceHanSerifSC-Bold.otf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/source-han-serif-sc/SourceHanSerifSC-Bold.otf',

  // Classic - Noto Serif SC (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-sc/NotoSerifSC-VF.woff2',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-sc/NotoSerifSC-VF.woff2',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-sc/NotoSerifSC-VF.woff2',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-sc/NotoSerifSC-VF.woff2',
}

// ---------------------------------------------------------------------------
// zh-TW (Traditional Chinese) fonts
// ---------------------------------------------------------------------------
// Playful: KuLeiWaYuanTi (self-hosted)
// Modern: Noto Sans TC (self-hosted)
// Technical: Source Han Serif TC (variable font, self-hosted)
// Classic: Noto Serif TC (self-hosted)

const ZH_TW_FONTS: TldrawFontUrls = {
  // Playful - KuLeiWaYuanTi
  tldraw_draw: 'https://padlet.net/fonts/kuleiwa-yuanti/KurewaGothicCjkJp-Regular-2.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/kuleiwa-yuanti/KurewaGothicCjkJp-Regular-2.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/kuleiwa-yuanti/KurewaGothicCjkJp-Bold-2.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/kuleiwa-yuanti/KurewaGothicCjkJp-Bold-2.ttf',

  // Modern - Noto Sans TC (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansTC-Regular.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansTC-Regular.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansTC-Bold.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansTC-Bold.ttf',

  // Technical - Source Han Serif TC (variable font, self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/source-han-serif-tc/SourceHanSerifTC-Regular.otf',
  tldraw_mono_italic: 'https://padlet.net/fonts/source-han-serif-tc/SourceHanSerifTC-Regular.otf',
  tldraw_mono_bold: 'https://padlet.net/fonts/source-han-serif-tc/SourceHanSerifTC-Bold.otf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/source-han-serif-tc/SourceHanSerifTC-Bold.otf',

  // Classic - Noto Serif TC (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-tc/NotoSerifTC-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-tc/NotoSerifTC-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-tc/NotoSerifTC-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-tc/NotoSerifTC-VF.ttf',
}

// ---------------------------------------------------------------------------
// ar/fa (Arabic/Persian) fonts
// ---------------------------------------------------------------------------
// Playful: Playpen Sans Arabic (self-hosted)
// Modern: Noto Sans Arabic (self-hosted)
// Technical: Cascadia Mono (self-hosted)
// Classic: Noto Naskh Arabic (self-hosted)

const AR_FA_FONTS: TldrawFontUrls = {
  // Playful - Playpen Sans Arabic
  tldraw_draw: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_italic: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_bold: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_bold_italic: PLAYPEN_SANS_ARABIC.variable,

  // Modern - Noto Sans Arabic (variable font, self-hosted)
  tldraw_sans: NOTO_SANS_ARABIC.variable,
  tldraw_sans_italic: NOTO_SANS_ARABIC.variable,
  tldraw_sans_bold: NOTO_SANS_ARABIC.variable,
  tldraw_sans_italic_bold: NOTO_SANS_ARABIC.variable,

  // Technical - Cascadia Mono (self-hosted)
  tldraw_mono: CASCADIA_MONO.variable,
  tldraw_mono_italic: CASCADIA_MONO.variableItalic,
  tldraw_mono_bold: CASCADIA_MONO.variable,
  tldraw_mono_italic_bold: CASCADIA_MONO.variableItalic,

  // Classic - Noto Naskh Arabic (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-naskh-arabic/Noto_Naskh_Arabic-Normal-Regular.woff2',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-naskh-arabic/Noto_Naskh_Arabic-Normal-Regular.woff2',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-naskh-arabic/Noto_Naskh_Arabic-Normal-Bold.woff2',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-naskh-arabic/Noto_Naskh_Arabic-Normal-Bold.woff2',
}

// ---------------------------------------------------------------------------
// ja (Japanese) fonts
// ---------------------------------------------------------------------------
// Playful: Hachi Maru Pop (self-hosted)
// Modern: Noto Sans JP (self-hosted)
// Technical: M PLUS 1 Code (self-hosted)
// Classic: Noto Serif JP (self-hosted)

const JA_FONTS: TldrawFontUrls = {
  // Playful - Hachi Maru Pop
  tldraw_draw: 'https://padlet.net/fonts/hachi-maru-pop/HachiMaruPop-Regular.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/hachi-maru-pop/HachiMaruPop-Regular.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/hachi-maru-pop/HachiMaruPop-Regular.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/hachi-maru-pop/HachiMaruPop-Regular.ttf',

  // Modern - Noto Sans JP (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansJP-Regular.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansJP-Regular.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansJP-Bold.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansJP-Bold.ttf',

  // Technical - M PLUS 1 Code (variable font, self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/mplus-1-code/MPLUS1Code-VF.ttf',
  tldraw_mono_italic: 'https://padlet.net/fonts/mplus-1-code/MPLUS1Code-VF.ttf',
  tldraw_mono_bold: 'https://padlet.net/fonts/mplus-1-code/MPLUS1Code-VF.ttf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/mplus-1-code/MPLUS1Code-VF.ttf',

  // Classic - Noto Serif JP (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-jp/Noto_Serif_JP-Normal-Regular.woff2',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-jp/Noto_Serif_JP-Normal-Regular.woff2',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-jp/Noto_Serif_JP-Normal-Bold.woff2',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-jp/Noto_Serif_JP-Normal-Bold.woff2',
}

// ---------------------------------------------------------------------------
// ko (Korean) fonts
// ---------------------------------------------------------------------------
// Playful: Chiron GoRound TC (self-hosted)
// Modern: Noto Sans KR (self-hosted)
// Technical: Nanum Gothic Coding (self-hosted)
// Classic: Noto Serif KR (self-hosted)

const KO_FONTS: TldrawFontUrls = {
  // Playful - Chiron GoRound TC
  tldraw_draw: 'https://padlet.net/fonts/chiron-goround-tc/ChironGoRoundTC-VF.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/chiron-goround-tc/ChironGoRoundTC-VF.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/chiron-goround-tc/ChironGoRoundTC-VF.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/chiron-goround-tc/ChironGoRoundTC-VF.ttf',

  // Modern - Noto Sans KR (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansKR-Regular.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansKR-Regular.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansKR-Bold.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansKR-Bold.ttf',

  // Technical - Nanum Gothic Coding (self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/nanum-gothic-coding/NanumGothicCoding-Regular.ttf',
  tldraw_mono_italic: 'https://padlet.net/fonts/nanum-gothic-coding/NanumGothicCoding-Regular.ttf',
  tldraw_mono_bold: 'https://padlet.net/fonts/nanum-gothic-coding/NanumGothicCoding-Bold.ttf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/nanum-gothic-coding/NanumGothicCoding-Bold.ttf',

  // Classic - Noto Serif KR (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-kr/NotoSerifKR-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-kr/NotoSerifKR-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-kr/NotoSerifKR-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-kr/NotoSerifKR-VF.ttf',
}

// ---------------------------------------------------------------------------
// th (Thai) fonts
// ---------------------------------------------------------------------------
// Playful: Playpen Sans Thai (self-hosted)
// Modern: Noto Sans Thai (self-hosted)
// Technical: Prompt (self-hosted)
// Classic: Noto Serif Thai (self-hosted)

const TH_FONTS: TldrawFontUrls = {
  // Playful - Playpen Sans Thai
  tldraw_draw: 'https://padlet.net/fonts/playpen-sans-thai/PlaypenSansThai-VF.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/playpen-sans-thai/PlaypenSansThai-VF.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/playpen-sans-thai/PlaypenSansThai-VF.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/playpen-sans-thai/PlaypenSansThai-VF.ttf',

  // Modern - Noto Sans Thai (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansThai-Regular.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansThai-Regular.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansThai-Bold.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansThai-Bold.ttf',

  // Technical - Prompt (variable font, self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/prompt/Prompt-Regular.ttf',
  tldraw_mono_italic: 'https://padlet.net/fonts/prompt/Prompt-Italic.ttf',
  tldraw_mono_bold: 'https://padlet.net/fonts/prompt/Prompt-Bold.ttf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/prompt/Prompt-BoldItalic.ttf',

  // Classic - Noto Serif Thai (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-thai/NotoSerifThai-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-thai/NotoSerifThai-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-thai/NotoSerifThai-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-thai/NotoSerifThai-VF.ttf',
}

// ---------------------------------------------------------------------------
// he (Hebrew) fonts
// ---------------------------------------------------------------------------
// Playful: Playpen Sans Hebrew (self-hosted)
// Modern: Noto Sans Hebrew (self-hosted)
// Technical: Cascadia Mono (self-hosted)
// Classic: Noto Serif Hebrew (self-hosted)

const HE_FONTS: TldrawFontUrls = {
  // Playful - Playpen Sans Hebrew
  tldraw_draw: 'https://padlet.net/fonts/playpen-sans-hebrew/PlaypenSansHebrew-VF.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/playpen-sans-hebrew/PlaypenSansHebrew-VF.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/playpen-sans-hebrew/PlaypenSansHebrew-VF.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/playpen-sans-hebrew/PlaypenSansHebrew-VF.ttf',

  // Modern - Noto Sans Hebrew (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansHebrew-VF.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansHebrew-VF.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansHebrew-VF.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansHebrew-VF.ttf',

  // Technical - Cascadia Mono (self-hosted, shared with ar/fa)
  tldraw_mono: CASCADIA_MONO.variable,
  tldraw_mono_italic: CASCADIA_MONO.variableItalic,
  tldraw_mono_bold: CASCADIA_MONO.variable,
  tldraw_mono_italic_bold: CASCADIA_MONO.variableItalic,

  // Classic - Noto Serif Hebrew (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-hebrew/Noto_Serif_Hebrew-Normal-Regular.woff2',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-hebrew/Noto_Serif_Hebrew-Normal-Regular.woff2',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-hebrew/Noto_Serif_Hebrew-Normal-Bold.woff2',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-hebrew/Noto_Serif_Hebrew-Normal-Bold.woff2',
}

// ---------------------------------------------------------------------------
// bn (Bengali) fonts
// ---------------------------------------------------------------------------
// Playful: Atma (self-hosted)
// Modern: Noto Sans Bengali (self-hosted)
// Technical: Hind Siliguri (self-hosted)
// Classic: Noto Serif Bengali (self-hosted)

const BN_FONTS: TldrawFontUrls = {
  // Playful - Atma
  tldraw_draw: 'https://padlet.net/fonts/atma/Atma-Regular.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/atma/Atma-Regular.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/atma/Atma-Bold.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/atma/Atma-Bold.ttf',

  // Modern - Noto Sans Bengali (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansBengali-Variable.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansBengali-Variable.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansBengali-Variable.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansBengali-Variable.ttf',

  // Technical - Hind Siliguri (variable font, self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/hind-siliguri/HindSiliguri-Regular.ttf',
  tldraw_mono_italic: 'https://padlet.net/fonts/hind-siliguri/HindSiliguri-Regular.ttf',
  tldraw_mono_bold: 'https://padlet.net/fonts/hind-siliguri/HindSiliguri-Bold.ttf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/hind-siliguri/HindSiliguri-Bold.ttf',

  // Classic - Noto Serif Bengali (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-bengali/NotoSerifBengali-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-bengali/NotoSerifBengali-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-bengali/NotoSerifBengali-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-bengali/NotoSerifBengali-VF.ttf',
}

// ---------------------------------------------------------------------------
// hi (Hindi) fonts
// ---------------------------------------------------------------------------
// Playful: Playpen Sans Deva (self-hosted)
// Modern: Noto Sans Devanagari (self-hosted)
// Technical: Akshar (self-hosted)
// Classic: Noto Serif Devanagari (self-hosted)

const HI_FONTS: TldrawFontUrls = {
  // Playful - Playpen Sans Deva
  tldraw_draw: 'https://padlet.net/fonts/playpen-sans-deva/PlaypenSansDeva-VF.ttf',
  tldraw_draw_italic: 'https://padlet.net/fonts/playpen-sans-deva/PlaypenSansDeva-VF.ttf',
  tldraw_draw_bold: 'https://padlet.net/fonts/playpen-sans-deva/PlaypenSansDeva-VF.ttf',
  tldraw_draw_bold_italic: 'https://padlet.net/fonts/playpen-sans-deva/PlaypenSansDeva-VF.ttf',

  // Modern - Noto Sans Devanagari (variable font, self-hosted)
  tldraw_sans: 'https://padlet.net/fonts/noto-sans/NotoSansDevanagari-VF.ttf',
  tldraw_sans_italic: 'https://padlet.net/fonts/noto-sans/NotoSansDevanagari-VF.ttf',
  tldraw_sans_bold: 'https://padlet.net/fonts/noto-sans/NotoSansDevanagari-VF.ttf',
  tldraw_sans_italic_bold: 'https://padlet.net/fonts/noto-sans/NotoSansDevanagari-VF.ttf',

  // Technical - Akshar (variable font, self-hosted)
  tldraw_mono: 'https://padlet.net/fonts/akshar/Akshar-VF.ttf',
  tldraw_mono_italic: 'https://padlet.net/fonts/akshar/Akshar-VF.ttf',
  tldraw_mono_bold: 'https://padlet.net/fonts/akshar/Akshar-VF.ttf',
  tldraw_mono_italic_bold: 'https://padlet.net/fonts/akshar/Akshar-VF.ttf',

  // Classic - Noto Serif Devanagari (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-serif-devanagari/NotoSerifDevanagari-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-serif-devanagari/NotoSerifDevanagari-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-serif-devanagari/NotoSerifDevanagari-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-serif-devanagari/NotoSerifDevanagari-VF.ttf',
}

// ---------------------------------------------------------------------------
// ur (Urdu) fonts
// ---------------------------------------------------------------------------
// Playful: Playpen Sans Arabic (self-hosted, shared with ar/fa)
// Modern: Noto Sans Arabic (self-hosted, shared with ar/fa)
// Technical: Cascadia Mono (self-hosted, shared with ar/fa/he)
// Classic: Noto Nastaliq Urdu (self-hosted)

const UR_FONTS: TldrawFontUrls = {
  // Playful - Playpen Sans Arabic (shared with ar/fa)
  tldraw_draw: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_italic: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_bold: PLAYPEN_SANS_ARABIC.variable,
  tldraw_draw_bold_italic: PLAYPEN_SANS_ARABIC.variable,

  // Modern - Noto Sans Arabic (shared with ar/fa)
  tldraw_sans: NOTO_SANS_ARABIC.variable,
  tldraw_sans_italic: NOTO_SANS_ARABIC.variable,
  tldraw_sans_bold: NOTO_SANS_ARABIC.variable,
  tldraw_sans_italic_bold: NOTO_SANS_ARABIC.variable,

  // Technical - Cascadia Mono (shared with ar/fa/he)
  tldraw_mono: CASCADIA_MONO.variable,
  tldraw_mono_italic: CASCADIA_MONO.variableItalic,
  tldraw_mono_bold: CASCADIA_MONO.variable,
  tldraw_mono_italic_bold: CASCADIA_MONO.variableItalic,

  // Classic - Noto Nastaliq Urdu (variable font, self-hosted)
  tldraw_serif: 'https://padlet.net/fonts/noto-nastaliq-urdu/NotoNastaliqUrdu-VF.ttf',
  tldraw_serif_italic: 'https://padlet.net/fonts/noto-nastaliq-urdu/NotoNastaliqUrdu-VF.ttf',
  tldraw_serif_bold: 'https://padlet.net/fonts/noto-nastaliq-urdu/NotoNastaliqUrdu-VF.ttf',
  tldraw_serif_italic_bold: 'https://padlet.net/fonts/noto-nastaliq-urdu/NotoNastaliqUrdu-VF.ttf',
}

// ---------------------------------------------------------------------------
// Partial override type (only overrides specific font styles)
// ---------------------------------------------------------------------------
type PartialTldrawFontUrls = Partial<TldrawFontUrls>

// ---------------------------------------------------------------------------
// bg/ru/sr/vi (Bulgarian/Russian/Serbian/Vietnamese) - Technical only
// ---------------------------------------------------------------------------
// Only Technical (mono) differs: IBM Plex Mono

const BG_RU_SR_VI_FONTS: PartialTldrawFontUrls = {
  // Technical - IBM Plex Mono (self-hosted)
  tldraw_mono: IBM_PLEX_MONO.regular,
  tldraw_mono_italic: IBM_PLEX_MONO.italic,
  tldraw_mono_bold: IBM_PLEX_MONO.bold,
  tldraw_mono_italic_bold: IBM_PLEX_MONO.boldItalic,
}

// ---------------------------------------------------------------------------
// el (Greek) - Playful, Technical, Classic only
// ---------------------------------------------------------------------------
// Playful: Playpen Sans (self-hosted)
// Technical: Cascadia Mono (self-hosted)
// Classic: Noto Serif (self-hosted)

const EL_FONTS: PartialTldrawFontUrls = {
  // Playful - Playpen Sans
  tldraw_draw: PLAYPEN_SANS.variable,
  tldraw_draw_italic: PLAYPEN_SANS.variable,
  tldraw_draw_bold: PLAYPEN_SANS.variable,
  tldraw_draw_bold_italic: PLAYPEN_SANS.variable,

  // Technical - Cascadia Mono
  tldraw_mono: CASCADIA_MONO.variable,
  tldraw_mono_italic: CASCADIA_MONO.variableItalic,
  tldraw_mono_bold: CASCADIA_MONO.variable,
  tldraw_mono_italic_bold: CASCADIA_MONO.variableItalic,

  // Classic - Noto Serif
  tldraw_serif: NOTO_SERIF.regular,
  tldraw_serif_italic: NOTO_SERIF.italic,
  tldraw_serif_bold: NOTO_SERIF.bold,
  tldraw_serif_italic_bold: NOTO_SERIF.boldItalic,
}

// ---------------------------------------------------------------------------
// tr (Turkish) - Classic only
// ---------------------------------------------------------------------------
// Classic: Noto Serif (self-hosted)

const TR_FONTS: PartialTldrawFontUrls = {
  // Classic - Noto Serif
  tldraw_serif: NOTO_SERIF.regular,
  tldraw_serif_italic: NOTO_SERIF.italic,
  tldraw_serif_bold: NOTO_SERIF.bold,
  tldraw_serif_italic_bold: NOTO_SERIF.boldItalic,
}

// ---------------------------------------------------------------------------
// uk (Ukrainian) - Technical, Classic only
// ---------------------------------------------------------------------------
// Technical: IBM Plex Mono (self-hosted)
// Classic: Noto Serif (self-hosted)

const UK_FONTS: PartialTldrawFontUrls = {
  // Technical - IBM Plex Mono
  tldraw_mono: IBM_PLEX_MONO.regular,
  tldraw_mono_italic: IBM_PLEX_MONO.italic,
  tldraw_mono_bold: IBM_PLEX_MONO.bold,
  tldraw_mono_italic_bold: IBM_PLEX_MONO.boldItalic,

  // Classic - Noto Serif
  tldraw_serif: NOTO_SERIF.regular,
  tldraw_serif_italic: NOTO_SERIF.italic,
  tldraw_serif_bold: NOTO_SERIF.bold,
  tldraw_serif_italic_bold: NOTO_SERIF.boldItalic,
}

/**
 * Returns locale-specific font URLs for tldraw, or null if the current locale
 * uses default fonts.
 */
function getLocaleFontUrls(): TldrawFontUrls | PartialTldrawFontUrls | null {
  const locale = getHyphenatedCurrentLocale()

  switch (locale) {
    // Full overrides (all 4 font styles)
    case 'zh-CN':
    case 'zh':
      return ZH_CN_FONTS
    case 'zh-TW':
      return ZH_TW_FONTS
    case 'ar':
    case 'fa':
      return AR_FA_FONTS
    case 'ja':
      return JA_FONTS
    case 'ko':
      return KO_FONTS
    case 'th':
      return TH_FONTS
    case 'he':
      return HE_FONTS
    case 'bn':
      return BN_FONTS
    case 'hi':
      return HI_FONTS
    case 'ur':
      return UR_FONTS

    // Partial overrides (only some font styles)
    case 'bg':
    case 'ru':
    case 'sr':
    case 'vi':
      return BG_RU_SR_VI_FONTS
    case 'el':
      return EL_FONTS
    case 'tr':
      return TR_FONTS
    case 'uk':
      return UK_FONTS

    default:
      return null
  }
}

/**
 * Preloads locale-specific font files into the browser cache.
 * Call this before mounting the Tldraw editor to avoid font flash
 * when locale fonts download after the initial render.
 */
async function preloadLocaleFonts(): Promise<void> {
  const localeFontUrls = getLocaleFontUrls()
  if (localeFontUrls == null) return

  const uniqueUrls = [...new Set(Object.values(localeFontUrls))]
  await Promise.allSettled(uniqueUrls.map((url) => fetch(url)))
}

export { getLocaleFontUrls, preloadLocaleFonts }
export type { TldrawFontUrls, PartialTldrawFontUrls }
